import { Component, inject, Input, OnDestroy } from '@angular/core';

import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { Portals } from '@qwyk/models';
import { SimpleModalComponent } from '@qwyk/ui-standalone';

import { QuotationOffersFacade } from './../../+state/quotation-offers.facade';

@Component({
    selector: 'qwyk-quotations-offer-list',
    templateUrl: './quotations-offers-list.component.html',
    styleUrls: ['./quotations-offers-list.component.scss'],
    providers: [DialogService]
})
export class QuotationsOffersListComponent implements OnDestroy {

    private modal = inject(DialogService);

    public pageSize = 10;
    public ref: DynamicDialogRef | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public search$: Subject<any> = new Subject<any>();
    private searchSubscription: Subscription;

    public error$ = this.quotationOffers.error$;
    public loading$ = this.quotationOffers.loading$;
    public pagination$ = this.quotationOffers.pagination$;
    public quotationOffers$ = this.quotationOffers.allQuotationOffers$;

    constructor(
        private translate: TranslateService,
        private quotationOffers: QuotationOffersFacade
    ) {
        this.searchSubscription = this.search$
            .pipe(debounceTime(200))
            .subscribe(query => {
                this.quotationOffers.init(this.quotation.id, query);
            });
    }

    private _quotation: Portals.QuotationEntity;

    public get quotation(): Portals.QuotationEntity {
        return this._quotation;
    }

    @Input()
    public set quotation(quotation: Portals.QuotationEntity) {
        this._quotation = quotation;
        this.search();
    }

    public onDelete(quotationOffer: Portals.QuotationOffer): void {
        this.ref = this.modal.open(SimpleModalComponent, {
            showHeader: false,
            closable: true,
            width: '50%',
            position: 'center',
            contentStyle: {
                'padding': 0
            },
            data: {
                header: this.translate.instant(
                    'hub.quotations.details.offer.delete-offer'
                ),
                message: this.translate.instant(
                    'hub.quotations.details.offer.delete-message'
                ),
                dismissAction: this.translate.instant(
                    'hub.quotations.details.offer.delete-dismiss-action'
                ),
                confirmAction: this.translate.instant(
                        'hub.quotations.details.offer.delete-confirmation-action'
                ),
                confirmStyle: 'btn-danger'
            },
        });

        this.ref.onClose.subscribe(res => {
            if (res) {
                this.quotationOffers.deleteQuotationOffer(
                    quotationOffer.quotation_id,
                    quotationOffer.id
                );
            }
        });
    }

    public onGeneratePdf(quotationOffer: Portals.QuotationOffer): void {
        this.quotationOffers.generateQuotationOfferPdf(
            quotationOffer.quotation_id,
            quotationOffer.id
        );
    }

    public onDismissError() {
        this.quotationOffers.dismissError();
    }

    public loadQuotationsOffersLazy(event: TableLazyLoadEvent): void {
        setTimeout(() => {
            let page = 1;
            if (event.first && event.rows) {
                page = event.first / event.rows + 1;
            }
            this.search(page);
        }, 50);
    }

    private search(page = 1) {
        const query = {
            pageSize: this.pageSize,
            page,
        };

        this.search$.next(query);
    }

    onToggleRow(offer: Portals.QuotationOffer, table: Table) {
        table.toggleRow(offer);
    }

    ngOnDestroy() {
        this.searchSubscription.unsubscribe();

        if (this.ref) {
            this.ref.close();
        }
    }
}
